import React, { useMemo, useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow, useCustom } from '@refinedev/core';
import { Space, Tag, Image, Tabs, theme } from 'antd';
import { EquipmentCatalogDto, EquipmentCatalogTranslationDto } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { isDefaultLocale, useActiveLocales } from '../../hooks/useActiveLocales';

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const LanguageContent: React.FC<{
  name?: string;
  description?: string;
  proTips?: string[] | string;
}> = ({ name, description, proTips }) => {
  const tips = Array.isArray(proTips)
    ? proTips
    : typeof proTips === 'string' && proTips.trim()
      ? proTips.split('\n').map((s) => s.trim()).filter(Boolean)
      : [];

  return (
    <div style={{ paddingTop: 8 }}>
      <Field label="Name">{name || '—'}</Field>
      <Field label="Description">
        {description ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>{description}</div>
        ) : '—'}
      </Field>
      <Field label="Pro Tips">
        {tips.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {tips.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        ) : '—'}
      </Field>
    </div>
  );
};

export const EquipmentCatalogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { locales } = useActiveLocales();
  const { queryResult } = useShow<EquipmentCatalogDto>({ resource: 'equipment-catalog' });
  const item = queryResult?.data?.data;
  const [activeLang, setActiveLang] = useState('en');

  const id = item?.id;

  const { data: imagesData } = useCustom({
    url: `/equipment-catalog/${id}/images`,
    method: 'get',
    queryOptions: {
      enabled: !!id,
    } as any,
  });
  const images = imagesData?.data ?? [];

  const translationLocales = useMemo(() => {
    const fromData = Object.keys(item?.translations || {}).filter((c) => !isDefaultLocale(c));
    const fromApi = locales.map((l) => l.code).filter((c) => !isDefaultLocale(c));
    return Array.from(new Set([...fromApi, ...fromData]));
  }, [item?.translations, locales]);

  const localeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locales) map[l.code] = l.label;
    const defaultLocale = locales.find((l) => isDefaultLocale(l.code));
    map.en = defaultLocale?.label || 'English';
    return map;
  }, [locales]);

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const tabItems = [
    {
      key: 'en',
      label: localeLabels.en,
      children: (
        <LanguageContent
          name={item.name}
          description={item.description}
          proTips={item.proTips}
        />
      ),
    },
    ...translationLocales.map((code) => {
      const data = (item.translations?.[code] || {}) as EquipmentCatalogTranslationDto;
      return {
        key: code,
        label: localeLabels[code] || code,
        children: (
          <LanguageContent
            name={data.name}
            description={data.description}
            proTips={data.proTips}
          />
        ),
      };
    }),
  ];

  return (
    <Show resource="equipment-catalog">
      <div style={{ padding: '8px 0' }}>
        <Tabs
          activeKey={activeLang}
          onChange={setActiveLang}
          items={tabItems}
          style={{ marginBottom: 24 }}
        />

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
          }}
        >
          <Field label="Brand">{item.brand}</Field>
          <Field label="Model">{item.model}</Field>
          <Field label="Category">
            <Tag style={tagStyle('#1890ff')}>{item.category}</Tag>
          </Field>
          <Field label="Difficulty Level">
            <Tag style={tagStyle('#f5a623')}>{item.difficultyLevel}</Tag>
          </Field>
          <Field label="Target Muscle Groups">
            {(item.targetMuscleGroups || []).length > 0 ? (
              <Space wrap>
                {(item.targetMuscleGroups || []).map((g: string) => (
                  <Tag key={g} style={{ borderRadius: 9999, padding: '2px 10px' }}>{g}</Tag>
                ))}
              </Space>
            ) : '—'}
          </Field>
          <Field label="Tags">
            {(item.tags || []).length > 0 ? (
              <Space wrap>
                {(item.tags || []).map((t: string) => (
                  <Tag key={t} style={{ borderRadius: 9999, padding: '2px 10px' }}>{t}</Tag>
                ))}
              </Space>
            ) : '—'}
          </Field>
          <Field label="Aliases">
            {(item.aliases || []).length > 0 ? (
              <Space wrap>
                {(item.aliases || []).map((a: string) => (
                  <Tag key={a} style={{ borderRadius: 9999, padding: '2px 10px' }}>{a}</Tag>
                ))}
              </Space>
            ) : '—'}
          </Field>
          <Field label="Image URLs">
            {(item.imageUrls || []).length > 0 ? (
              <Space direction="vertical">
                {(item.imageUrls || []).map((url: string, i: number) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">{url}</a>
                ))}
              </Space>
            ) : '—'}
          </Field>
          <Field label="Video Links">
            {(item.videoLinks || []).length > 0 ? (
              <Space direction="vertical">
                {(item.videoLinks || []).map((url: string, i: number) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">{url}</a>
                ))}
              </Space>
            ) : '—'}
          </Field>

          {item.suggestedWorkout && (
            <Field label="Suggested Workout">
              <div>
                <div><strong>Sets:</strong> {item.suggestedWorkout.sets}</div>
                <div><strong>Reps:</strong> {item.suggestedWorkout.reps}</div>
                <div><strong>Weight:</strong> {item.suggestedWorkout.recommendedWeight}</div>
                <div><strong>Rest:</strong> {item.suggestedWorkout.restBetweenSets}</div>
                <div><strong>Frequency:</strong> {item.suggestedWorkout.frequency}</div>
                {item.suggestedWorkout.variations?.length > 0 && (
                  <div><strong>Variations:</strong> {item.suggestedWorkout.variations.join(', ')}</div>
                )}
              </div>
            </Field>
          )}

          {item.formGuide && (
            <Field label="Form Guide">
              <div>
                <div><strong>Setup:</strong> {item.formGuide.setup}</div>
                <div><strong>Execution:</strong> {item.formGuide.execution}</div>
                {(Array.isArray(item.formGuide.commonMistakes) ? item.formGuide.commonMistakes : []).length > 0 && (
                  <div><strong>Common Mistakes:</strong> {item.formGuide.commonMistakes.join(', ')}</div>
                )}
                {(Array.isArray(item.formGuide.tips) ? item.formGuide.tips : []).length > 0 && (
                  <div><strong>Tips:</strong> {item.formGuide.tips.join(', ')}</div>
                )}
              </div>
            </Field>
          )}

          <Field label="Active">
            <Tag style={tagStyle(item.isActive ? '#3ecf8e' : '#898989')}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Field>

          <Field label="Reference Images">
            {images.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {images.map((img: any) => (
                  <Image
                    key={img.id}
                    src={img.imageUrl}
                    alt="Reference"
                    width={120}
                    height={120}
                    style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #e8e8e8' }}
                    preview={{ mask: 'View' }}
                  />
                ))}
              </div>
            ) : '—'}
          </Field>

          <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
          <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
        </div>
      </div>
    </Show>
  );
};
